import {
  DefaultTransformer,
  deserialize,
  DocumentObject,
  JsonObject,
  transform,
  Transformer,
} from '@jsonapiworld/jsonapi-serializer';
import isNil from 'lodash/isNil';
import { backendApiGateway } from '~/client/backend';
import { CompanyResourceType } from '~/types/company';
import { CounterpartResourceType } from '~/types/counterparts';
import { DocumentResourceType } from '~/types/document';
import { FlowResourceType } from '~/types/flow';
import {
  BookkeepingBankTransaction,
  BookkeepingTransaction,
  BookkeepingTransactionFilters,
  BookkeepingTransactionSorting,
  BookkeepingTransactionsResponse,
  BulkAcceptBookkeepingTransactionsResponse,
  BulkRegisterBookkeepingTransactionsPayload,
  BulkRegisterBookkeepingTransactionsResponse,
  CreateBookkeepingAdjustmentPayload,
  CreateBookkeepingEntry,
  CreateBookkeepingTransaction,
  UpdateBookkeepingTransaction,
} from '~/types/general-bookkeeping';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Money } from '~/types/money';
import { TransactionResourceType, TransactionsFiltersFields } from '~/types/transaction';
import { formatDateForAPI, formatISOForAPI } from '~/utils/formatDate';
import {
  buildJsonAPIQueryParams,
  jsonAPICursorPaginatorBuilder,
  jsonAPIFilterBuilder,
  jsonAPIIncludeBuilder,
  jsonAPISortBuilder,
  SortInfo,
} from '~/utils/jsonapi-query';
import { createTransactionsFilterBuilder } from '../transaction';

const transactionsPageSize = 50;

interface FetchTransactionsOptions {
  size?: number;
  cursor?: string;
  sorting?: SortInfo<BookkeepingTransactionSorting>;
}

export const fetchBookkeepingTransactions = async (
  companyId: string,
  filters: BookkeepingTransactionFilters = {},
  options?: FetchTransactionsOptions,
): Promise<BookkeepingTransactionsResponse> => {
  const includeBuilder: BookkeepingTransactionIncludeOption[] = [
    'company',
    'bookkeepingEntries',
    'bookkeepingEntries.bookkeepingAccount',
    'bookkeepingEntries.bookkeepingAdjustment',
    'bookkeepingEntries.document',
    'bookkeepingEntries.counterpart',
    'bookkeepingEntries.counterpart.parent',
    'transactions',
    'vatRegistration',
    'vatRegistration.documents',
    'vatRegistration.documents.counterpart',
    'vatRegistration.vatBook',
    'vatRegistration.vatReportingPeriod',
    'vatRegistration.vatEntries',
    'vatRegistration.vatEntries.vatCode',
    'vatRegistration.vatEntries.bookkeepingAccount',
    'vatRegistration.vatRegistrationBills',
    'vatRegistration.vatRegistrationBills.vatCodes',
    'vatRegistration.vatRegistrationBills.vatCodes.vatCode',
    'vatRegistration.vatRegistrationBills.vatCodes.bookkeepingAccount',
    'vatSettlementPeriod',
  ];

  const filtersBuilder = jsonAPIFilterBuilder();
  filtersBuilder.eq('company.id', companyId);

  if (filters.dateFrom) {
    filtersBuilder.gte('date', formatISOForAPI(filters.dateFrom));
  }
  if (filters.dateTo) {
    filtersBuilder.lte('date', formatISOForAPI(filters.dateTo));
  }
  if (filters.description) {
    filtersBuilder.eq('description', filters.description);
  }
  if (filters.accountId) {
    filtersBuilder.eq('hasBookkeepingAccount', filters.accountId);
  }
  if (filters.counterpartId) {
    filtersBuilder.eq('hasCounterpart', filters.counterpartId);
  }
  if (filters.search) filtersBuilder.ilike('search', filters.search);

  if (!isNil(filters.amountRange)) {
    if (!isNil(filters.amountRange[0])) filtersBuilder.gte('bookkeepingEntryAmount', filters.amountRange[0]);
    if (!isNil(filters.amountRange[1])) filtersBuilder.lte('bookkeepingEntryAmount', filters.amountRange[1]);
  }

  if (!isNil(filters.cashBookAmountRange)) {
    if (!isNil(filters.cashBookAmountRange[0]))
      filtersBuilder.gte('bookkeepingCashBookAmount', filters.cashBookAmountRange[0]);
    if (!isNil(filters.cashBookAmountRange[1]))
      filtersBuilder.lte('bookkeepingCashBookAmount', filters.cashBookAmountRange[1]);
  }

  if (filters.transactionDescription) {
    filtersBuilder.ilike('transaction.description', filters.transactionDescription);
  }

  if (filters.adjustmentType?.length) {
    filtersBuilder.in('adjustmentType', filters.adjustmentType);
  }

  const dateDirection = options?.sorting?.direction ?? 'asc';
  const sortBuilder = jsonAPISortBuilder([
    { sort: 'date', direction: dateDirection },
    { sort: 'number', direction: dateDirection },
    { sort: 'id', direction: dateDirection },
  ]);

  const paginationParamsBuilder = jsonAPICursorPaginatorBuilder();
  paginationParamsBuilder.size(options?.size ?? transactionsPageSize);
  if (options?.cursor) paginationParamsBuilder.cursor(options.cursor);

  const { data: results } = await backendApiGateway.get<DocumentObject & { meta: { page: MetaDataCursorPage } }>(
    '/api/v1/bookkeeping-transactions',
    {
      params: buildJsonAPIQueryParams(
        filtersBuilder,
        jsonAPIIncludeBuilder(includeBuilder),
        paginationParamsBuilder,
        sortBuilder,
      ),
    },
  );

  return {
    transactions: deserialize<BookkeepingTransaction>(results) as BookkeepingTransaction[],
    page: (results.meta as { page: MetaDataCursorPage }).page,
  };
};

type BookkeepingTransactionIncludeOption =
  | 'company'
  | 'bookkeepingEntries'
  | 'bookkeepingEntries.bookkeepingAccount'
  | 'bookkeepingEntries.bookkeepingAdjustment'
  | 'bookkeepingEntries.bookkeepingAdjustment.bookkeepingAccount'
  | 'bookkeepingEntries.flow'
  | 'bookkeepingEntries.document'
  | 'bookkeepingEntries.counterpart'
  | 'bookkeepingEntries.counterpart.parent'
  | 'transactions'
  | 'vatRegistration'
  | 'vatRegistration.documents'
  | 'vatRegistration.documents.counterpart'
  | 'vatRegistration.vatBook'
  | 'vatRegistration.vatEntries'
  | 'vatRegistration.vatEntries.bookkeepingAccount'
  | 'vatRegistration.vatEntries.vatCode'
  | 'vatRegistration.vatReportingPeriod'
  | 'vatRegistration.vatRegistrationBills'
  | 'vatRegistration.vatRegistrationBills.vatCodes'
  | 'vatRegistration.vatRegistrationBills.vatCodes.vatCode'
  | 'vatRegistration.vatRegistrationBills.vatCodes.bookkeepingAccount'
  | 'vatSettlementPeriod';

interface FetchBookkeepingTransactionOptions {
  include: BookkeepingTransactionIncludeOption[];
}

export const fetchBookkeepingTransaction = async (
  id: BookkeepingTransaction['id'],
  options: FetchBookkeepingTransactionOptions,
) => {
  const includeBuilder = jsonAPIIncludeBuilder(options.include);
  const params = buildJsonAPIQueryParams(includeBuilder);

  const { data: result } = await backendApiGateway.get<DocumentObject>(`/api/v1/bookkeeping-transactions/${id}`, {
    params,
  });

  return deserialize<BookkeepingTransaction>(result) as BookkeepingTransaction;
};

interface BookkeepingEntriesMetadata {
  count: number;
  startBalance: Money;
}

interface BookkeepingEntriesMetadataParams {
  company_id: string;
  bookkeeping_account_id: string;
  from_date: string;
  to_date: string;
  counterpart_id?: string;
}

export const fetchBookkeepingEntriesMetadata = async (
  companyId: string,
  filters: BookkeepingTransactionFilters = {},
) => {
  const params: BookkeepingEntriesMetadataParams = {
    company_id: companyId,
    bookkeeping_account_id: filters.accountId!,
    from_date: formatDateForAPI(new Date(filters.dateFrom!)),
    to_date: formatDateForAPI(new Date(filters.dateTo!)),
  };
  if (filters.counterpartId) {
    params.counterpart_id = filters.counterpartId;
  }

  const { data: result } = await backendApiGateway.get<BookkeepingEntriesMetadata>(
    `/api/v1/bookkeeping-ledger-cards/metadata`,
    {
      params,
    },
  );

  return result;
};

export type AdjustmentPayloadWithLid = CreateBookkeepingAdjustmentPayload & { lid: string };

type AdjustmentTransactionInput = Omit<CreateBookkeepingTransaction, 'companyId'> & { lid: string };

export class CreateBookkeepingAdjustmentTransformer extends Transformer<AdjustmentPayloadWithLid, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-adjustment';
    this.relationships = {
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      bookkeepingTransactions: ({ bookkeepingTransactions, lid: adjLid }) =>
        transform<AdjustmentTransactionInput, {}>()
          .withInput(
            bookkeepingTransactions.map((tx, txIdx) => ({
              ...tx,
              lid: `${adjLid}-tx-${txIdx}`,
              bookkeepingEntries: tx.bookkeepingEntries.map((entry, entryIdx) => ({
                ...entry,
                lid: `${adjLid}-tx-${txIdx}-entry-${entryIdx}`,
              })),
            })),
          )
          .withTransformer(new CreateAdjustmentTransactionTransformer())
          .withIncluded(true)
          .toContext(),
    };
  }

  transform({ lid, adjustmentType, sourceType }: AdjustmentPayloadWithLid): JsonObject {
    return { lid, adjustmentType, sourceType };
  }
}

class CreateAdjustmentTransactionTransformer extends Transformer<AdjustmentTransactionInput, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-transaction';
    this.relationships = {
      bookkeepingEntries: ({ bookkeepingEntries }) =>
        transform<CreateBookkeepingEntry, {}>()
          .withInput(bookkeepingEntries as CreateBookkeepingEntry[])
          .withTransformer(new CreateBookkeepingEntryTransformer())
          .withIncluded(true)
          .toContext(),
    };
  }

  transform({ lid, date, description }: AdjustmentTransactionInput): JsonObject {
    return { lid, date: formatDateForAPI(date), description };
  }
}

class CreateBookkeepingEntryTransformer extends Transformer<CreateBookkeepingEntry, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-entry';
    this.relationships = {
      bookkeepingAccount: ({ bookkeepingAccountId }) =>
        transform()
          .withInput({ id: bookkeepingAccountId })
          .withTransformer(new DefaultTransformer('bookkeeping-account'))
          .toContext(),
      counterpart: ({ counterpartId }) =>
        transform()
          .withInput(counterpartId ? { id: counterpartId } : null)
          .withTransformer(new DefaultTransformer(CounterpartResourceType))
          .toContext(),
      document: ({ documentId }) =>
        transform()
          .withInput(documentId ? { id: documentId } : null)
          .withTransformer(new DefaultTransformer(DocumentResourceType))
          .toContext(),
      flow: ({ flowId }) =>
        transform()
          .withInput(flowId ? { id: flowId } : null)
          .withTransformer(new DefaultTransformer(FlowResourceType))
          .toContext(),
      bookkeepingAdjustment: ({ bookkeepingAdjustment, lid }) => {
        if (!bookkeepingAdjustment) {
          return transform()
            .withInput(null)
            .withTransformer(new DefaultTransformer('bookkeeping-adjustment'))
            .toContext();
        }
        return transform<AdjustmentPayloadWithLid, {}>()
          .withInput({ ...bookkeepingAdjustment, lid: `adj-${lid}` })
          .withTransformer(new CreateBookkeepingAdjustmentTransformer())
          .withIncluded(true)
          .toContext();
      },
    };
  }

  transform(payload: CreateBookkeepingEntry): JsonObject {
    return {
      lid: payload.lid,
      amount: payload.amount,
      description: payload.description,
      source: payload.source,
      competencePeriodStart: payload.competencePeriodStart
        ? formatDateForAPI(payload.competencePeriodStart)
        : undefined,
      competencePeriodEnd: payload.competencePeriodEnd ? formatDateForAPI(payload.competencePeriodEnd) : undefined,
    };
  }
}

class CreateBookkeepingTransactionTransformer extends Transformer<CreateBookkeepingTransaction, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-transaction';
    this.relationships = {
      company: ({ companyId }) =>
        transform()
          .withInput({ id: companyId })
          .withTransformer(new DefaultTransformer(CompanyResourceType))
          .toContext(),
      bookkeepingEntries: ({ bookkeepingEntries }) =>
        transform<CreateBookkeepingEntry, {}>()
          .withInput(
            bookkeepingEntries.map((entry, index) => {
              return {
                lid: index.toString(),
                bookkeepingAccountId: entry.bookkeepingAccountId,
                counterpartId: entry.counterpartId,
                documentId: entry.documentId,
                flowId: entry.flowId,
                amount: entry.amount,
                description: entry.description,
                source: entry.source,
                competencePeriodStart: entry.competencePeriodStart ?? undefined,
                competencePeriodEnd: entry.competencePeriodEnd ?? undefined,
                bookkeepingAdjustment: entry.bookkeepingAdjustment,
              } satisfies CreateBookkeepingEntry;
            }),
          )
          .withTransformer(new CreateBookkeepingEntryTransformer())
          .withIncluded(true)
          .toContext(),
      transactions: ({ transactionIds }) =>
        transform()
          .withInput(transactionIds.map((id) => ({ id })))
          .withTransformer(new DefaultTransformer(TransactionResourceType))
          .toContext(),
    };
  }

  transform(payload: CreateBookkeepingTransaction): JsonObject {
    return {
      date: formatDateForAPI(payload.date),
      description: payload.description,
      recommendedReconciliationFlowId: payload.recommendedReconciliationFlowId,
    };
  }
}

export const createBookkeepingTransaction = async (
  values: CreateBookkeepingTransaction,
): Promise<BookkeepingTransaction> => {
  const payload = transform<CreateBookkeepingTransaction, {}>()
    .withInput(values)
    .withTransformer(new CreateBookkeepingTransactionTransformer())
    .serialize();

  const includeOptions: BookkeepingTransactionIncludeOption[] = ['bookkeepingEntries', 'transactions'];

  const includeBuilder = jsonAPIIncludeBuilder(includeOptions);
  const searchParams = buildJsonAPIQueryParams(includeBuilder);

  const { data: result } = await backendApiGateway.post<DocumentObject>('/api/v1/bookkeeping-transactions', payload, {
    params: searchParams,
  });
  return deserialize<BookkeepingTransaction>(result) as BookkeepingTransaction;
};

class UpdateBookkeepingTransactionTransformer extends Transformer<UpdateBookkeepingTransaction, {}> {
  constructor() {
    super();
    this.type = 'bookkeeping-transaction';
    this.relationships = {
      bookkeepingEntries: ({ bookkeepingEntries }) =>
        transform<CreateBookkeepingEntry, {}>()
          .withInput(
            bookkeepingEntries.map((entry, index) => {
              return {
                lid: index.toString(),
                bookkeepingAccountId: entry.bookkeepingAccountId,
                counterpartId: entry.counterpartId,
                documentId: entry.documentId,
                flowId: entry.flowId,
                amount: entry.amount,
                description: entry.description,
                source: entry.source,
                competencePeriodStart: entry.competencePeriodStart ?? undefined,
                competencePeriodEnd: entry.competencePeriodEnd ?? undefined,
                bookkeepingAdjustment: entry.bookkeepingAdjustment,
              } satisfies CreateBookkeepingEntry;
            }),
          )
          .withTransformer(new CreateBookkeepingEntryTransformer())
          .withIncluded(true)
          .toContext(),
    };
  }
  transform(payload: UpdateBookkeepingTransaction): JsonObject {
    return {
      date: formatDateForAPI(payload.date),
      description: payload.description,
    };
  }
}

export const updateBookkeepingTransaction = async (
  id: BookkeepingTransaction['id'],
  values: UpdateBookkeepingTransaction,
): Promise<BookkeepingTransaction> => {
  const payload = transform<UpdateBookkeepingTransaction, {}>()
    .withInput(values)
    .withTransformer(new UpdateBookkeepingTransactionTransformer())
    .serialize();
  const { data: result } = await backendApiGateway.patch<DocumentObject>(
    `/api/v1/bookkeeping-transactions/${id}`,
    payload,
  );
  return deserialize<BookkeepingTransaction>(result) as BookkeepingTransaction;
};

export const deleteBookkeepingTransaction = async (id: BookkeepingTransaction['id']): Promise<void> => {
  await backendApiGateway.delete(`/api/v1/bookkeeping-transactions/${id}`);
};

export const bulkRegisterBookkeepingTransactions = async ({
  companyId,
  transactionsFilters,
  attributes,
}: {
  companyId: string;
  transactionsFilters: TransactionsFiltersFields;
  attributes: BulkRegisterBookkeepingTransactionsPayload['attributes'];
}): Promise<BulkRegisterBookkeepingTransactionsResponse> => {
  const filtersBuilder = createTransactionsFilterBuilder({
    companyId,
    ...transactionsFilters,
  });
  const { data: result } = await backendApiGateway.post<BulkRegisterBookkeepingTransactionsResponse>(
    '/api/v1/bookkeeping-transactions/bulk-register',
    {
      companyId,
      attributes,
    },
    {
      params: buildJsonAPIQueryParams(filtersBuilder),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return result;
};

export const bulkAcceptBookkeepingTransactions = async (
  companyId: string,
  transactions: BookkeepingBankTransaction[],
) => {
  const transactionIds: string[] = transactions.map((t) => t.id);

  const { data: result } = await backendApiGateway.post<BulkAcceptBookkeepingTransactionsResponse>(
    '/api/v1/bookkeeping-transactions/bulk-accept',
    { companyId, transactionIds },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return result;
};
